<!--
  CelebrationBurst.vue — dependency-free one-shot firework of brand star marks and
  gold coins, radiating outward from the success modal's icon circle. Coin styling
  adapted from the make-it-rain layer in Components/SgaCampaignOverlayGold.vue.

  Bursts are keyed off `trigger`, not a boolean `active`: every change spawns an
  independent, self-removing burst instance rather than toggling one shared
  element off/on. That lets rapid repeat clicks (e.g. 1-star vote spam) stack
  overlapping fireworks instead of each click killing the previous burst mid-flight.
-->
<script setup>
import { ref, watch } from 'vue'
import StarMarkSVG from './StarMarkSVG.vue'

const props = defineProps({
    trigger: { type: [Number, String], default: 0 },
})

// Deterministic pseudo-random spread (same seeding trick as SgaCampaignOverlayGold)
// — identical for every burst instance, so overlapping bursts reinforce the same
// trajectories rather than clashing into a messy scatter.
const N = 32
const particles = []
for (let i = 0; i < N; i++) {
    const s = i * 7.13 + 3
    const angle = (i / N) * Math.PI * 2 + ((s * 19) % 40) / 100
    const distance = 90 + ((s * 23) % 130)
    particles.push({
        id: i,
        isStar: i % 3 !== 0,
        size: 8 + ((s * 5) % 10),
        tx: Math.round(Math.cos(angle) * distance),
        ty: Math.round(Math.sin(angle) * distance),
        rot: i % 2 === 0 ? 320 : -280,
        delay: ((s * 13) % 25) / 100,
        dur: 0.9 + ((s * 7) % 60) / 100,
    })
}

// Longest possible particle finishes by ~0.25s delay + ~1.5s duration; clear a
// little after so a burst never lingers in the DOM once it's fully faded out.
const BURST_LIFETIME_MS = 2000

const bursts = ref([])
let nextId = 0

watch(() => props.trigger, (val, oldVal) => {
    if (val === oldVal) return

    const id = nextId++
    bursts.value.push(id)
    setTimeout(() => {
        bursts.value = bursts.value.filter((b) => b !== id)
    }, BURST_LIFETIME_MS)
})
</script>

<template>
    <div v-for="burstId in bursts" :key="burstId" class="celebration" aria-hidden="true">
        <span
            v-for="p in particles"
            :key="p.id"
            class="celebration__particle"
            :class="p.isStar ? 'celebration__particle--star' : 'celebration__particle--coin'"
            :style="{
                width: p.size + 'px',
                height: p.size + 'px',
                '--tx': p.tx + 'px',
                '--ty': p.ty + 'px',
                '--rot': p.rot + 'deg',
                animationDelay: p.delay + 's',
                animationDuration: p.dur + 's',
            }"
        >
            <StarMarkSVG v-if="p.isStar" :size="p.size" />
        </span>
    </div>
</template>

<style scoped>
/* Anchored at the center of the modal's 56px icon circle (36px card padding + 28px) */
.celebration {
    position: absolute;
    top: 64px; left: 50%;
    width: 0; height: 0;
    pointer-events: none;
    z-index: 1;
}
.celebration__particle {
    position: absolute; top: 0; left: 0;
    will-change: transform, opacity;
    opacity: 0;
    animation-name: celebration-burst;
    animation-iteration-count: 1;
    animation-timing-function: cubic-bezier(0.16, 0.6, 0.45, 1);
    animation-fill-mode: forwards;
}
.celebration__particle--coin {
    border-radius: 50%;
    background: radial-gradient(circle at 32% 28%, #FFF7DC 0%, #FDE68A 22%, #FBBF24 55%, #B45309 100%);
    box-shadow:
        0 0 6px rgba(251,191,36,0.55),
        inset 0 1px 0 rgba(255,255,255,0.55),
        inset 0 -2px 2px rgba(180,83,9,0.5);
}
.celebration__particle--star {
    display: flex; align-items: center; justify-content: center;
    filter: drop-shadow(0 0 4px rgba(252,211,77,0.7));
}
.celebration__particle--star :deep(svg) {
    display: block; width: 100%; height: 100%;
}
@keyframes celebration-burst {
    0% {
        transform: translate(0, 0) scale(0.3) rotate(0deg);
        opacity: 0;
    }
    12% {
        transform: translate(calc(var(--tx) * 0.35), calc(var(--ty) * 0.35)) scale(1) rotate(calc(var(--rot) * 0.3));
        opacity: 1;
    }
    100% {
        transform: translate(var(--tx), calc(var(--ty) + 60px)) scale(0.85) rotate(var(--rot));
        opacity: 0;
    }
}
@media (prefers-reduced-motion: reduce) {
    .celebration__particle { animation: none; opacity: 0; }
}
</style>
